import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Check, ChevronDown } from "lucide-react";

export interface DropdownOption {
  value: string;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface DropdownProps {
  value: string | null;
  options: DropdownOption[];
  placeholder?: string;
  leftIcon?: React.ReactNode;
  invalid?: boolean;
  disabled?: boolean;
  onSelect: (value: string) => void;
  /** Copilot anchor for the trigger button; options get `${dataCy}Option-<value>`. */
  dataCy?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  value,
  options,
  placeholder = "Select an option",
  leftIcon,
  invalid,
  disabled,
  onSelect,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        data-cy={dataCy}
        aria-invalid={invalid}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-lg border border-solid bg-background px-3 text-sm",
          invalid ? "border-destructive" : "border-border",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        {leftIcon}
        <span className={cn("flex-1 truncate text-left", selected ? "text-foreground" : "text-slate-400")}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="text-slate-400 h-4 w-4 shrink-0" />
      </button>
      {open && (
        <div className="border-border absolute left-0 right-0 top-11 z-30 max-h-64 overflow-auto rounded-lg border border-solid bg-popover py-1 shadow-[0px_4px_12px_rgba(2,6,23,0.12)]">
          {options.length === 0 ? (
            <p className="text-slate-400 px-3 py-2 text-sm">No options available</p>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  onSelect(option.value);
                  setOpen(false);
                }}
                data-cy={dataCy ? `${dataCy}Option-${option.value}` : undefined}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                  option.disabled
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted"
                )}
              >
                {option.icon}
                <span className="flex flex-1 flex-col overflow-hidden">
                  <span className="truncate">{option.label}</span>
                  {option.sublabel && <span className="text-slate-400 truncate text-xs">{option.sublabel}</span>}
                </span>
                {option.value === value && (
                  <Check className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
